import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {MatTooltip} from '@angular/material/tooltip';
import {MatCheckbox, MatCheckboxChange} from '@angular/material/checkbox';
import {ITreeSelectFormElement} from '../form-data.interface';
import {SelectionModel} from '@angular/cdk/collections';
import {TreeComponent, TreeModel, TreeModule, TreeNode} from '@ali-hm/angular-tree-component';
import {TreeOptions} from '@ali-hm/angular-tree-component/lib/models/tree-options.model';
import {SelectElementComponent} from '../select-element/select-element.component';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {clamp} from 'frontier/nucleus';
import {CdkConnectedOverlay, CdkOverlayOrigin, FlexibleConnectedPositionStrategy} from '@angular/cdk/overlay';
import {ICustomTreeNode, INodeData} from '../../../../tree-control/node-data.interface';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatButton} from '@angular/material/button';
import {ClearEntityComponent} from 'frontier/browserkit/src/lib/components/clear-entity/clear-entity.component';
import {
  CreateNewEntityComponent
} from 'frontier/browserkit/src/lib/components/create-new-entity/create-new-entity.component';
import {SearchInputComponent} from '../../search-input/search-input.component';
import {CdkTrapFocus} from '@angular/cdk/a11y';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {TreeSelectFormDirective} from './tree-select-form.directive';
import {MatInput} from '@angular/material/input';
import {MatIcon} from '@angular/material/icon';
import {NgClass, NgIf} from '@angular/common';
import {MatFormField, MatLabel, MatPrefix, MatSuffix} from '@angular/material/form-field';

@Component({
  selector: 'kpi4me-tree-select',
  templateUrl: './tree-select.component.html',
  styleUrls: ['./tree-select.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: true,
  imports: [
    MatFormField,
    NgIf,
    MatIcon,
    MatPrefix,
    MatLabel,
    MatInput,
    TreeSelectFormDirective,
    FormsModule,
    CdkOverlayOrigin,
    ReactiveFormsModule,
    MatSuffix,
    CdkConnectedOverlay,
    CdkTrapFocus,
    SearchInputComponent,
    TreeModule,
    MatCheckbox,
    NgClass,
    MatTooltip,
    CreateNewEntityComponent,
    ClearEntityComponent,
    MatButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
  ],
})
export class TreeSelectComponent extends SelectElementComponent implements AfterViewInit, OnDestroy {
  private _elementRef: ElementRef<any> = inject(ElementRef);
  private _snackBar: MatSnackBar = inject(MatSnackBar);
  @ViewChild('tree', {static: false}) tree: TreeComponent;
  @ViewChild('inputTrigger', {static: true}) inputTrigger: ElementRef;
  @ViewChild('panelRef', {static: false}) panelRef: ElementRef;
  @ViewChild(CdkConnectedOverlay, {static: false}) cdkConnectedOverlay: CdkConnectedOverlay;

  config: any;
  @Input() nodes: any;
  override emptyOptionAdded: boolean = true;

  @Input() columnName: string;
  @Input() recursiveSelection: boolean = true;

  treeOptions: Partial<TreeOptions> = {
    get idField(): string {
      return 'signature';
    },
  };

  cellPosition: DOMRect;

  @Output() containerSizeChange: EventEmitter<any> = new EventEmitter();
  isOpen: boolean = false;
  createRootEnabled: boolean = false;
  private closed: boolean = false;

  @Input()
  override set data(form: ITreeSelectFormElement) {
    if (form.emptyOptionAdded) {
      this.nodes = [{name: ' '}, ...form.nodes];
    } else {
      this.nodes = form.nodes;
    }
    this.emptyOptionAdded = form.emptyOptionAdded;
    this.createNewEnabled = form.createNewEnabled;
    this.editEnabled = form.editElementEnabled;
    this.deleteEnabled = form.deleteElementEnabled;
    this.createRootEnabled = form.createRootEnabled;
    this.clearEnabled = form.clearEnabled;
    this.recursiveSelection = form.recursiveSelection == null ? true : form.recursiveSelection;

    this.getFormControl().setValue(form.value);

    super.data = form;
    this._selection = new SelectionModel<any>(form.multiple, form.value);

  }

  private _selection: SelectionModel<any>;
  override arrowPosition: 'left' | 'right';
  menuPos: { x: number, y: number } = {
    x: 0,
    y: 0
  };
  positionStrategy: FlexibleConnectedPositionStrategy;

  @Input() set selection(s: SelectionModel<any>) {
    if (s != null) {
      this._selection = new SelectionModel<any>(s.isMultipleSelection());
      s.selected.forEach((nodeData): void => {
        if (nodeData && nodeData.signature) {
          const node: ICustomTreeNode = this.tree.treeModel.getNodeById(
            nodeData.signature
          );
          if (node) {
            this.selectNode(node, true);
          }
        }
      });
    }
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.cellPosition = this._elementRef.nativeElement.getBoundingClientRect();
  }

  get selection():
    SelectionModel<INodeData> {
    return this._selection;
  }

  ngOnDestroy(): void {
    this.isOpen = false;
  }

  /**
   * Selects a node. The checked attribute of the node is set to the selection state, and also the
   * selection model is updated.
   * @param node
   * @param isSet
   * @param recursive
   * @private
   */
  private selectNode(
    node: ICustomTreeNode,
    isSet: boolean,
    recursive: boolean = this.recursiveSelection,
  ): void {
    if (!node?.data) {
      return;
    }

    if (node.data.is_selectable === false) {
      this._snackBar.open(
        'Dieser Knoten kann nicht ausgewählt werden, da er vom falschen Typ ist.',
        'ok',
        {duration: 5000, verticalPosition: 'top'},
      );
      return;
    }

    // Try to set the isLeaf property correctly if it is missing for any reason
    node.data.isleaf = node.data.isleaf ?? !(node?.children?.length > 0);
    node.checked = isSet;

    // Handle selection based on node type and selection state
    if (isSet) {
      if (node.data.isleaf || !recursive) {
        this.selection.select(node.data);
      }
    } else {
      this.selection.deselect(node.data);
    }

    // Handle recursive selection for parent nodes
    if (!node.data.isleaf && node.children && recursive) {
      node.children.forEach((childNode: ICustomTreeNode) => {
        this.selectNode(childNode, isSet, recursive);
      });
    }

    // Update parent selection state
    if (node.parent && recursive) {
      this.selectParentNodePartially(node.parent);
    }

    console.log(node.data, node.checked);
  }

  /**
   * For a parent node, checks if a child node is selected or every child node is selected.
   * @private
   * @param parent
   */
  private selectParentNodePartially(parent: ICustomTreeNode): void {
    const allChildrenSelected = parent.children.reduce(
      (previousValue: ICustomTreeNode, currentValue: ICustomTreeNode) => {
        return previousValue && currentValue.checked;
      },
      true
    );
    if (allChildrenSelected) {
      parent.checked = true;
      parent.partiallyChecked = false;
    } else {
      parent.checked = false;
      this.selection.deselect(parent.data);
      if (
        parent.children.findIndex(
          (childNode: ICustomTreeNode): boolean =>
            childNode.checked || childNode.partiallyChecked
        ) != -1
      ) {
        parent.partiallyChecked = true;
      } else {
        parent.partiallyChecked = false;
      }
    }
    if (parent.parent != undefined) {
      this.selectParentNodePartially(parent.parent);
    }
  }

  onNodeSelectionChange(evt: MatCheckboxChange, node: ICustomTreeNode): void {
    this.cdr.detach();
    node.checked = node.checked == true;
    if (!node.checked) {
      // check the node as selected
      if (this.selection.isMultipleSelection()) {
        this.selectNode(node, true);
      } else {
        // deselect all nodes from the tree model
        this.tree.treeModel.doForAll((n: ICustomTreeNode): void => {
          if (n.data.signature != node.data.signature) {
            n.checked = false;
          }
        });
        this.selection.clear();
        this.selectNode(node, true);
      }
    } else {
      // deselect the node
      this.selectNode(node, false);
    }
    this.cdr.reattach();
    this.cdr.detectChanges();
    // this.selectionChange.emit(this.selection);
    this.getFormControl()?.setValue(this.selection.selected)
  }

  onGetNodeTooltip(node: any, myTooltip: MatTooltip
  ): void {

  }

  getNodeTooltip(node: any
  ) {
    return node.data.name;
  }

  onNewNode(parent: any, index: any, param3: any, param4: boolean
  ): void {

  }

  showCheckbox(node: any
  ): boolean {
    return true;
  }

  override onSearchChange($event: any): void {
    console.log($event)
    this.tree.treeModel.filterNodes($event);
  }

  onFocusIn(): void {
    if (this.closed === false) {
      this.isOpen = true;
    }
    this.closed = false;
    this.cdr.detectChanges();
  }

  onTreeInit(): void {
    let model: SelectionModel<unknown> = new SelectionModel(this.multiple);
    model.select(...this.getFormControl().value);
    this.selection = model;
    const currentlySelectedNodeData: INodeData[] = this.getFormControl().value;
    if (currentlySelectedNodeData && currentlySelectedNodeData.length > 0) {
      const currentlySelectedNodes: ICustomTreeNode[] = currentlySelectedNodeData
        .map((nodeData: INodeData): ICustomTreeNode => this.tree.treeModel.getNodeById(nodeData.signature));
      if (currentlySelectedNodes && currentlySelectedNodes.length > 0) {
        currentlySelectedNodes.forEach((node: ICustomTreeNode): void => node.ensureVisible());
        currentlySelectedNodes[0].focus();
      } else {
        this.tree.treeModel.expandAll();
      }
    } else {
      this.tree.treeModel.expandAll();
    }
    setTimeout((): void => {
      this.onResize()
    }, 0)
  }

  onNodeContextMenu(event: MouseEvent, menuTrigger: MatMenuTrigger): void {
    if (this.createNewEnabled === false) {
      return;
    }

    this.menuPos = {
      x: event.clientX,
      y: event.clientY
    }

    menuTrigger.openMenu();
  }

  override onConfirm(): void {
    console.log('confirmed selection: ', this.selection.selected);
    this.confirm.emit(this.selection.selected)
  }

  deleteNode(node: TreeNode): void {
    node.parent.data.children.splice(node.parent.data.children.indexOf(node.data), 1)
    this.tree.treeModel.update();
    if (node && node.parent && node.parent.data && node.parent.data.children.length === 0) {
      node.parent.data.hasChildren = false;
    }
  }

  onNodeActivate(evt: { eventName: string, node: TreeNode, treeModel: TreeModel }): void {
    console.log(evt);
    if (this.selection.isMultipleSelection() === false) {
      if (evt.node.data.is_selectable === false) {
        this._snackBar.open(
          'Dieser Knoten kann nicht ausgewählt werden, da er vom falschen Typ ist.',
          'ok',
          {duration: 5000, verticalPosition: 'top'},
        );
      } else {
        this.selectNode(evt.node as unknown as ICustomTreeNode, true);
        this.getFormControl().setValue([evt.node.data]);

        this.onConfirm();
      }
    }
  }

  onBackdropClick(event: MouseEvent): void {
    this.isOpen = false
    this.cdr.detectChanges();
  }

  getColumnTitle(): void {
    // pass a column name
  }

  toggleDropdown(): void {
    this.closed = true;
    this.isOpen = false;
    this.cdr.detectChanges();
    console.log(this.isOpen);
  }

  onResize(): void {
    const margin: number = 10;
    const {
      top,
      left,
      bottom,
      right,
      width,
      height
    } = this.cdkConnectedOverlay.overlayRef.overlayElement.getBoundingClientRect();
    const {innerHeight, innerWidth} = window;
    let x: number = clamp(left, 0 + margin, innerWidth - margin);
    x = clamp(left, 0 + margin, innerWidth - width - margin);
    let y: number = clamp(top, 0 + margin, innerHeight - margin);
    y = clamp(top, 0 + margin, innerHeight - height - margin);

    this.cdkConnectedOverlay.overlayRef.overlayElement.style.left = `${x}px`
    this.cdkConnectedOverlay.overlayRef.overlayElement.style.top = `${y}px`
  }
}
